import * as types from '../types';
import { isModuleSubscriptionProduct } from '../types/Product';
import { FiCountry } from '../contexts/LocaleContext';
import { isAxiosError, webApiClient } from './fi-api/apiUtils';
import { logInternalError } from './errors';
import { getProductsBySku } from './product';

export function cartHasSomePhysicalProducts(cart: types.Cart, products: types.IProduct[]) {
  const productMap = getProductsBySku(products);
  return Object.values(cart.cartItems).some((cartItem) => {
    const product = productMap.get(cartItem.lineItem.sku);
    return product && product.physical;
  });
}

export function isBundleCartItem(cartItem: types.CartItem): cartItem is types.BundleCartItem {
  return 'subscriptionLineItem' in cartItem;
}

export function isGiftCardCartItem(cartItem: types.CartItem): cartItem is types.GiftCardCartItem {
  return 'giftCardDelivery' in cartItem;
}

export function isForModuleCartItem(
  cartItem: types.CartItem,
): cartItem is types.ForModuleBundleCartItem | types.ForModuleCartItem {
  return 'forModuleId' in cartItem;
}

export function isUpgradeCartItem(cartItem: types.CartItem): cartItem is types.Series3UpgradeCartItem {
  return isBundleCartItem(cartItem) && isForModuleCartItem(cartItem);
}

export function skusForCartItem(cartItem: types.CartItem) {
  if (isBundleCartItem(cartItem)) {
    const addonSkus = cartItem.addonLineItems?.map((lineItem) => lineItem.sku) ?? [];
    return [cartItem.lineItem.sku, cartItem.subscriptionLineItem.sku, ...addonSkus];
  }

  return [cartItem.lineItem.sku];
}

export function giftCardCartItems(cart: types.Cart) {
  return Object.values(cart.cartItems).filter(isGiftCardCartItem);
}

export function cartHasItems(cart: types.Cart) {
  return Object.keys(cart.cartItems).length > 0;
}

/**
 * Returns true if ALL bundles in the cart contain free trial subscriptions.
 */
export function cartContainsOnlyFreeTrialBundles(cart: types.Cart, products: types.IProduct[]): boolean {
  const bundles = Object.values(cart.cartItems).filter(isBundleCartItem);
  if (bundles.length === 0) {
    return false;
  }

  const productMap = getProductsBySku(products);
  return bundles.every((cartItem) => {
    const subscriptionProduct = productMap.get(cartItem.subscriptionLineItem.sku);
    return subscriptionProduct && isModuleSubscriptionProduct(subscriptionProduct) && subscriptionProduct.freeTrial;
  });
}

/**
 * Returns true if ANY bundle in the cart contains a free trial subscription.
 */
export function cartContainsAnyFreeTrialBundle(cart: types.Cart, products: types.IProduct[]): boolean {
  const bundles = Object.values(cart.cartItems).filter(isBundleCartItem);
  if (bundles.length === 0) {
    return false;
  }

  const productMap = getProductsBySku(products);
  return bundles.some((cartItem) => {
    const subscriptionProduct = productMap.get(cartItem.subscriptionLineItem.sku);
    return subscriptionProduct && isModuleSubscriptionProduct(subscriptionProduct) && subscriptionProduct.freeTrial;
  });
}

/**
 * Validates whether items in the cart are available for purchase in the
 * given country. Returns IDs of invalid cart items so the caller can
 * remove them and notify the user.
 */
export async function validateCart(cart: types.Cart, country: FiCountry): Promise<string[]> {
  try {
    await webApiClient.post('/api/ecommerce/validate-cart', {
      cartItems: Object.values(cart.cartItems),
      countryCode: country,
    });
    return [];
  } catch (err) {
    if (isAxiosError(err) && err.response?.data?.error?.cartItemIds) {
      return err.response.data.error.cartItemIds;
    }
    logInternalError(err, { tags: { source: 'validateCart' } });
    throw err;
  }
}
